import { valueExists } from '../../../common/utils/utils';
import {
	formatExpiryBatchSummaryText,
	formatExpiryBatchesText,
	normalizeDateKey,
} from '../../../common/utils/warehousing/formatExpiryBatchSummary';

const PACKAGING_LABELS = {
	pallet: { singular: 'pallet', plural: 'pallets' },
	shipper: { singular: 'shipper', plural: 'shippers' },
	case: { singular: 'case', plural: 'cases' },
	unit: { singular: 'unit', plural: 'units' },
};

export const normalizeBarcode = (value) => (value || '').trim();

export const patchStocktakeItemInList = (items, itemId, patch) => {
	return items.map(item => ((item.id || item._id) === itemId ? { ...item, ...patch } : item));
};

const getPackagingBreakdown = (units, warehouseProduct) => {
	const breakdown = [];
	const forceQuantityInUnits = Boolean(warehouseProduct?.product?.forceQuantityInUnits);
	if (forceQuantityInUnits) {
		return [{ type: 'unit', count: Math.max(0, Number(units) || 0) }];
	}

	const caseSize = warehouseProduct?.caseSize || warehouseProduct?.product?.caseSize || 1;
	const shipperSize = warehouseProduct?.product?.shipperSize || 0;
	const palletSize = warehouseProduct?.product?.palletSize || 0;
	const hasCases = caseSize > 1;

	let remainder = Math.max(0, Number(units) || 0);

	if (hasCases && palletSize > 0) {
		const palletUnits = palletSize * caseSize;
		if (palletUnits > 0) {
			const palletCount = Math.floor(remainder / palletUnits);
			if (palletCount > 0) {
				breakdown.push({ type: 'pallet', count: palletCount });
				remainder %= palletUnits;
			}
		}
	}

	if (hasCases && shipperSize > 0) {
		const shipperUnits = shipperSize * caseSize;
		if (shipperUnits > 0) {
			const shipperCount = Math.floor(remainder / shipperUnits);
			if (shipperCount > 0) {
				breakdown.push({ type: 'shipper', count: shipperCount });
				remainder %= shipperUnits;
			}
		}
	}

	if (hasCases && caseSize > 1) {
		const caseCount = Math.floor(remainder / caseSize);
		if (caseCount > 0) {
			breakdown.push({ type: 'case', count: caseCount });
			remainder %= caseSize;
		}
	}

	if (remainder > 0) {
		breakdown.push({ type: 'unit', count: remainder });
	}
	else if (!breakdown.length) {
		breakdown.push({ type: 'unit', count: 0 });
	}

	return breakdown;
};

export const formatQuantityWithPackaging = (quantity, warehouseProduct) => {
	if (!valueExists(quantity)) {
		return null;
	}

	const sign = quantity < 0 ? '-' : '';
	const absQuantity = Math.abs(quantity);
	const unitLabel = absQuantity === 1 ? PACKAGING_LABELS.unit.singular : PACKAGING_LABELS.unit.plural;
	const baseLabel = `${sign}${absQuantity} ${unitLabel}`;

	if (absQuantity === 0) {
		return baseLabel;
	}

	const breakdown = getPackagingBreakdown(absQuantity, warehouseProduct);
	const hasPackagingVariety = breakdown.some(part => part.type !== 'unit');

	if (!hasPackagingVariety) {
		return baseLabel;
	}

	const packagingParts = breakdown.map(part => {
		const labelConfig = PACKAGING_LABELS[part.type] || PACKAGING_LABELS.unit;
		const pluralLabel = part.count === 1 ? labelConfig.singular : labelConfig.plural;
		return `${sign}${part.count} ${pluralLabel}`;
	});

	return `${baseLabel} (${packagingParts.join(', ')})`;
};

export const findStocktakeItemByBarcode = (items, barcode) => {
	// EAN-8, UPC-A, EAN-13 and zero-padded GTIN-14 can identify the same product.
	const normalizeForLookup = (value) => {
		const code = normalizeBarcode(value);
		return /^(\d{8}|\d{12,14})$/.test(code) ? code.padStart(14, '0') : code;
	};
	const normalized = normalizeForLookup(barcode);

	if (!normalized) {
		return null;
	}

	const findByCode = (matches) => items.find(item => {
		const barcodes = item.warehouseProduct?.product?.barcodes || [];
		return barcodes.some(code => matches(normalizeForLookup(code)));
	});

	// Warehouse cartons carry a GTIN-14 (packaging indicator 1-8) that wraps the unit code
	// with a new check digit, so match it to the unit code when no product saved the carton code.
	const unitCodeWithoutCheckDigit = /^[1-8]\d{13}$/.test(normalized) ? `0${normalized.slice(1, 13)}` : null;

	return findByCode(code => code === normalized) ||
		(unitCodeWithoutCheckDigit && findByCode(code => /^0\d{13}$/.test(code) && code.slice(0, 13) === unitCodeWithoutCheckDigit)) ||
		null;
};

export const normalizeStocktakeBatchRows = (rows = [], timezone) => {
	return rows
		.map(row => ({
			quantity: Math.max(0, Math.round(Number(row?.quantityOnHand ?? row?.quantity) || 0)),
			expiryDate: normalizeDateKey(row?.expiryDate, timezone) || '',
			supplierBatchCode: row?.supplierBatchCode ? String(row.supplierBatchCode).trim() : '',
		}))
		.filter(row => row.quantity > 0 || row.expiryDate || row.supplierBatchCode);
};

const collapseStocktakeBatchRows = (rows = []) => {
	const collapsedRowsByKey = new Map();

	rows.forEach((row) => {
		const rowQuantity = Math.max(0, Math.round(Number(row?.quantity) || 0));
		const expiryDate = row?.expiryDate || '';
		const supplierBatchCode = row?.supplierBatchCode || '';
		const rowKey = `${expiryDate}__${supplierBatchCode}`;
		const existingRow = collapsedRowsByKey.get(rowKey);

		if (existingRow) {
			existingRow.quantity += rowQuantity;
			return;
		}

		collapsedRowsByKey.set(rowKey, {
			quantity: rowQuantity,
			expiryDate,
			supplierBatchCode,
		});
	});

	return [...collapsedRowsByKey.values()].filter(row => row.quantity > 0 || row.expiryDate || row.supplierBatchCode);
};

const getBatchRowsTotalQuantity = (rows = []) => {
	return rows.reduce((acc, row) => acc + Math.max(0, Math.round(Number(row?.quantity) || 0)), 0);
};

const hasAnyBatchExpiryDate = (rows = []) => {
	return rows.some(row => Boolean(row?.expiryDate));
};

const getWarehouseBatchRowsFromSummary = (item) => {
	const summaryRows = item?.warehouseProduct?.expiryBatchSummary?.rows || [];
	const unknownQuantity = Math.max(0, Math.round(Number(item?.warehouseProduct?.expiryBatchSummary?.unknownQuantity) || 0));

	const mappedRows = summaryRows.map(row => ({
		quantity: Math.max(0, Math.round(Number(row?.quantity) || 0)),
		expiryDate: row?.expiryDate || '',
		supplierBatchCode: '',
	}));

	if (unknownQuantity > 0) {
		mappedRows.push({
			quantity: unknownQuantity,
			expiryDate: '',
			supplierBatchCode: '',
		});
	}

	return collapseStocktakeBatchRows(mappedRows);
};

export const resolveBestCurrentWarehouseBatchRows = ({
	item,
	currentWarehouseRows = [],
}) => {
	const normalizedCurrentRows = collapseStocktakeBatchRows(currentWarehouseRows);
	const summaryRows = getWarehouseBatchRowsFromSummary(item);
	const currentHasKnownDates = hasAnyBatchExpiryDate(normalizedCurrentRows);
	const summaryHasKnownDates = hasAnyBatchExpiryDate(summaryRows);
	const currentTotalQuantity = getBatchRowsTotalQuantity(normalizedCurrentRows);
	const summaryTotalQuantity = getBatchRowsTotalQuantity(summaryRows);

	if (!currentHasKnownDates && summaryHasKnownDates &&
		(!currentTotalQuantity || !summaryTotalQuantity || currentTotalQuantity === summaryTotalQuantity)) {
		return summaryRows;
	}

	if (normalizedCurrentRows.length) {
		return normalizedCurrentRows;
	}

	return summaryRows;
};

export const getWarehouseProductTimezone = (warehouseProduct, fallbackTimezone) => (
	warehouseProduct?.tenants?.[0]?.timezone ||
	warehouseProduct?.rootTenant?.timezone ||
	fallbackTimezone
);

const getCountedStocktakeBatchRows = (item, timezone) => collapseStocktakeBatchRows(normalizeStocktakeBatchRows(item?.countedExpiryBatches || [], timezone));

export const getCurrentWarehouseBatchRows = (item, timezone) => collapseStocktakeBatchRows(normalizeStocktakeBatchRows(item?.warehouseProduct?.expiryBatches || [], timezone));

export const getStocktakeBatchRowsForDialog = (item, currentWarehouseRowsOverride, timezone) => {
	const countedRows = getCountedStocktakeBatchRows(item, timezone);
	const currentRows = resolveBestCurrentWarehouseBatchRows({
		item,
		currentWarehouseRows: currentWarehouseRowsOverride || getCurrentWarehouseBatchRows(item, timezone),
	});

	if (countedRows.length) {
		const countedHasKnownDates = hasAnyBatchExpiryDate(countedRows);
		const currentHasKnownDates = hasAnyBatchExpiryDate(currentRows);
		const countedTotalQuantity = getBatchRowsTotalQuantity(countedRows);
		const currentTotalQuantity = getBatchRowsTotalQuantity(currentRows);

		if (!countedHasKnownDates && currentHasKnownDates && countedTotalQuantity === currentTotalQuantity) {
			return currentRows;
		}

		return countedRows;
	}

	if (currentRows.length) {
		return currentRows;
	}

	const fallbackQuantity = Math.max(0, Math.round(Number(item?.quantity) || 0));
	return [{ quantity: fallbackQuantity, expiryDate: '', supplierBatchCode: '' }];
};

export const getStocktakeBatchSummaryText = (item) => {
	const timezone = getWarehouseProductTimezone(item?.warehouseProduct);
	const countedRows = getCountedStocktakeBatchRows(item, timezone);
	if (countedRows.length) {
		return `Counted: ${formatExpiryBatchesText(countedRows, { fallback: 'n/a', maxRows: 2, timezone })}`;
	}

	return `Current: ${formatExpiryBatchSummaryText(item?.warehouseProduct?.expiryBatchSummary, { fallback: 'n/a', maxRows: 2 })}`;
};

export const resolveDefaultExpiryDateForNewStocktakeBatchRow = ({
	rows = [],
	currentWarehouseRows = [],
}) => {
	for (let rowIndex = rows.length - 1; rowIndex >= 0; rowIndex -= 1) {
		const rowDate = rows[rowIndex]?.expiryDate;
		if (rowDate) {
			return rowDate;
		}
	}

	const firstWarehouseDate = (currentWarehouseRows || []).find(row => row?.expiryDate)?.expiryDate;
	return firstWarehouseDate || '';
};
